/**
 * Public API of the consent layer.
 *
 * Replaces gatsby-plugin-gdpr-cookies: the trackers are injected by our own code
 * (see google.js / linkedin.js) so that consent, Consent Mode v2 and the CSP are
 * all under our control.
 */
import { useSyncExternalStore } from 'react';
import { PURPOSES } from './config';
import {
    clearConsent,
    getConsent,
    getServerConsent,
    hydrate,
    setConsent,
    subscribe,
} from './store';
import { bootGoogle, revokeGoogle, trackGooglePageView } from './google';
import { bootLinkedIn } from './linkedin';

export { PURPOSES };

function applyConsent(state) {
    bootGoogle(state);
    bootLinkedIn(state);
}

/** Called once from gatsby-browser's onClientEntry. */
export function bootstrapConsent() {
    const state = hydrate();
    if (state.decided) applyConsent(state);
}

/** Called from gatsby-browser's onRouteUpdate — including the initial render. */
export function trackPageView(location) {
    trackGooglePageView(location);
}

/**
 * Records a decision and starts (or reconfigures) the trackers.
 * A page view is sent here because no route change follows an in-page decision.
 */
export function saveConsent(selection) {
    const state = setConsent(selection);
    applyConsent(state);
    trackGooglePageView(window.location);
    return state;
}

/**
 * Withdraws consent: stops storage, deletes the first-party cookies we can reach
 * and reloads so already-loaded third-party scripts are gone.
 */
export function revokeConsent() {
    revokeGoogle();
    clearConsent();
    window.location.reload();
}

/** Subscribes a component to the current decision. */
export function useConsent() {
    return useSyncExternalStore(subscribe, getConsent, getServerConsent);
}
